<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";

import type {
  IntegrationStatus,
} from "@/types/integrationInstance";

import { IntegrationState, DriverType } from "@/types/enums";

import { useComponentErrorCapture } from "@/mixins/error";
import translatedProperty from "@/mixins/translatedProperty";

import SelectedIcon from "@/components/elements/icon/SelectedIcon.vue";
import Dialog from "@/components/elements/Dialog.vue";

useComponentErrorCapture();

const router = useRouter();

const props = defineProps({
  integration: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(["startNotConfigured", "delete"]);

const dialogDelete = ref();

const isActive = computed(() => {
  return (props.integration as IntegrationStatus).state == IntegrationState.ACTIVE || (props.integration as IntegrationStatus).state == IntegrationState.CONNECTED;
});

const showDeleteButton = computed(() => {
  const int = props.integration;
  return (int.driver_type == DriverType.LOCAL && (int.instance_count > 0 || typeof int.state != 'undefined')) ||
  (int.driver_type == DriverType.EXTERNAL) || 
  (int.driver_type == DriverType.CUSTOM);
});

function goto() {
  if (!props.integration.integration_id) {
    emit('startNotConfigured', props.integration);
  } else {
    router.push({
      name: "integration",
      params: { integration_id: props.integration.integration_id },
    })
  }
}

function startDelete() {
  dialogDelete.value.open();
}

function deleteItem() {
  emit('delete', props.integration);
}

</script>
<template>
  <div 
    class="ent-item"
    @click="goto">
    <div class="ent-item__header">
      <SelectedIcon :icon="integration.icon || 'uc:puzzle'" />
    </div>
    <div class="ent-item__text">
      <h3 class="ent-item__title">{{ translatedProperty(integration.name) }}</h3>
    </div>
    <div class="ent-item__footer">
      <div class="ent-item__attributes">
        <span
          v-if="integration.instance_count < 1"
          class="ent-item__attribute ent-item__attribute--green">
          <i class="fa-light fa-circle-plus"></i>
        </span>
        <span
          v-else-if="integration.state"
          class="ent-item__attribute "
          :class="isActive ? 'ent-item__attribute--green' :  'ent-item__attribute--red'"
          :title="integration.state ? $t(`integration.status.${integration.state}`) : $t(`integration.status.NOT_CONFIGURED`)">
          <i class="fa-light" :class="isActive ? 'fa-circle-check' :  'fa-circle-xmark'"></i>
        </span>
        <span
          v-if="integration.driver_type === DriverType.CUSTOM || integration.driver_type === DriverType.EXTERNAL"
          :title="integration.driver_type === DriverType.CUSTOM ? $t('integration.driver_type.custom') : $t('integration.driver_type.external')"
          class="ent-item__attribute ent-item__attribute--wide">
          <i v-if="integration.driver_type === DriverType.CUSTOM" class="fa-light fa-user-group"></i>
          <i v-else class="fa-light fa-server"></i>
        </span>
      </div>
      <div class="ent-item__options">
        <button
          v-if="showDeleteButton"
          @click.stop="startDelete"
          class="button button--delete button--icon button--icon--medium">
          <i class="fa-light fa-trash"></i>
        </button>
      </div>
    </div>
  </div>
  <Dialog
    ref="dialogDelete"
    @submit="deleteItem"
    :title="$t('integration.delete.title')"
    :text="$t('integration.delete.question')"
    :submit-text="$t('ui.delete')"
    :cancel-text="$t('ui.cancel')"
  />
</template>
